import * as React from 'react';
import {
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceNotice,
  workspaceFieldClass,
} from '@/components/ui/workspace';
import {
  createFramework,
  nameTakenFromError,
  type FrameworkSummary,
} from '@/lib/api/discovery-frameworks';
import { cn } from '@/lib/utils/cn';
import { messageOf, type DraftDifference } from './framework-logic';
import { CautionNote, LINK_BUTTON } from './parts';

/**
 * The dialogs of the Discovery frameworks screen (T-0184): publish with a
 * change note (prototype `dialog()` kind `fw-publish`), and New framework,
 * which copies an existing one (E12-F01 scenario 30).
 */

export const MIN_CHANGE_NOTE = 5;

/** What changed in the draft under the curator (rule 2), one line per item. */
export function DifferencesList({ items }: { items: readonly DraftDifference[] }) {
  if (items.length === 0) return null;
  return (
    <ul
      aria-label="What changed"
      className="max-h-[200px] flex-none overflow-y-auto rounded-card border border-ink-100 bg-paper-0"
    >
      {items.map((d, i) => (
        <li
          key={`${d.what}:${d.code ?? d.title}:${i}`}
          className="grid grid-cols-[72px_112px_56px_minmax(0,1fr)] gap-2.5 border-b border-ink-100 px-3 py-2 text-meta last:border-b-0"
        >
          <span className="font-semibold text-ink-700">{d.action}</span>
          <span className="truncate text-ink-700">{d.what}</span>
          <span className="font-mono text-ink-700">{d.code ?? ''}</span>
          <span className="truncate text-ink-700" title={d.title}>
            {d.title}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function PublishDialog({
  open,
  onOpenChange,
  frameworkName,
  nextVersion,
  corroborationOff,
  summary,
  stale,
  publishing,
  error,
  onPublish,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  frameworkName: string;
  nextVersion: number;
  corroborationOff: boolean;
  /** What this version changes, one phrase per kind. */
  summary: readonly string[];
  /** Set when the draft changed under the curator: what changed. */
  stale: readonly DraftDifference[] | null;
  publishing: boolean;
  error: string | null;
  onPublish: (changeNote: string) => void;
}) {
  const [note, setNote] = React.useState('');
  React.useEffect(() => {
    if (open) setNote('');
  }, [open]);
  const canPublish = note.trim().length >= MIN_CHANGE_NOTE && !publishing;

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Publish ${frameworkName} v${nextVersion}`}
      description="New engagements start on this version. New interview types and questions are additive, so running engagements get them for interviews they add from now on. Rules and areas stay on each engagement’s copy until its lead moves it."
      actions={
        <>
          <WorkspaceButton size="md" variant="tertiary" onClick={() => onOpenChange(false)}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="primary"
            disabled={!canPublish}
            onClick={() => onPublish(note.trim())}
          >
            {publishing ? 'Publishing…' : 'Publish'}
          </WorkspaceButton>
        </>
      }
    >
      {stale ? (
        <>
          <WorkspaceNotice tone="at-risk">
            <strong className="font-semibold text-ink-900">The draft changed.</strong> Someone else
            saved to it while you were working, so nothing was published. Here is what they changed.
            Read it, then publish.
          </WorkspaceNotice>
          <DifferencesList items={stale} />
        </>
      ) : null}
      {corroborationOff ? (
        <CautionNote>
          <b className="font-semibold">Corroboration is turned off in this version:</b> one source
          covers an area. Engagements moved to it will say so.
        </CautionNote>
      ) : null}
      {summary.length > 0 ? (
        <p className="text-body-sm text-ink-700">This version: {summary.join(', ')}.</p>
      ) : null}
      <div>
        <label
          htmlFor="fw-publish-note"
          className="mb-1.5 block text-meta font-medium text-ink-700"
        >
          Change note <span className="font-normal text-posture-critical">*</span>
        </label>
        <textarea
          id="fw-publish-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What changed and why"
          autoFocus
          maxLength={2000}
          rows={3}
          className={cn(workspaceFieldClass, 'resize-y py-2 leading-5')}
        />
        <p className="mt-1.5 text-meta text-ink-500">
          At least {MIN_CHANGE_NOTE} characters. It is shown in History.
        </p>
      </div>
      {error ? <WorkspaceNotice tone="critical">{error}</WorkspaceNotice> : null}
    </WorkspaceDialog>
  );
}

export function NewFrameworkDialog({
  open,
  onOpenChange,
  frameworks,
  defaultSourceId,
  onCreated,
  onOpenExisting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  frameworks: readonly FrameworkSummary[];
  defaultSourceId: string | null;
  onCreated: (id: string, name: string) => void;
  onOpenExisting: (id: string) => void;
}) {
  const sources = frameworks.filter((f) => f.current_version > 0);
  const [name, setName] = React.useState('');
  const [sourceId, setSourceId] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<React.ReactNode | null>(null);
  React.useEffect(() => {
    if (!open) return;
    setName('');
    setError(null);
    setSourceId(
      sources.some((f) => f.id === defaultSourceId) ? defaultSourceId! : (sources[0]?.id ?? ''),
    );
    // Only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const create = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await createFramework(name.trim(), sourceId);
      onCreated(created.id, created.name);
    } catch (err) {
      const existing = nameTakenFromError(err)?.existing;
      setError(
        existing && frameworks.some((f) => f.id === existing.id) ? (
          <>
            {messageOf(err, 'The framework could not be created.')}{' '}
            <button
              type="button"
              className={LINK_BUTTON}
              onClick={() => onOpenExisting(existing.id)}
            >
              Open {existing.name}
            </button>
          </>
        ) : (
          messageOf(err, 'The framework could not be created.')
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title="New framework"
      description="It starts as a draft copy of another framework, with its own codes. Engagements can use it once you publish it."
      actions={
        <>
          <WorkspaceButton size="md" variant="tertiary" onClick={() => onOpenChange(false)}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="primary"
            disabled={saving || !name.trim() || !sourceId}
            onClick={() => void create()}
          >
            {saving ? 'Creating…' : 'Create and start editing'}
          </WorkspaceButton>
        </>
      }
    >
      <div>
        <label htmlFor="fw-new-name" className="mb-1.5 block text-meta font-medium text-ink-700">
          Name <span className="font-normal text-posture-critical">*</span>
        </label>
        <input
          id="fw-new-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. County finance"
          autoFocus
          autoComplete="off"
          maxLength={120}
          className={cn(workspaceFieldClass, 'h-9')}
        />
      </div>
      <div>
        <label htmlFor="fw-new-source" className="mb-1.5 block text-meta font-medium text-ink-700">
          Copy from
        </label>
        <select
          id="fw-new-source"
          value={sourceId}
          onChange={(e) => setSourceId(e.target.value)}
          className={cn(workspaceFieldClass, 'h-9 pr-8')}
        >
          {sources.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name} v{f.current_version}
            </option>
          ))}
        </select>
      </div>
      {error ? (
        <p role="alert" className="text-meta text-posture-critical">
          {error}
        </p>
      ) : null}
    </WorkspaceDialog>
  );
}
